import { times } from "lodash-es";
import { Component } from "react";
import { toast } from "extensions/toast";
import { connect } from "react-redux";
import printJS from "print-js";

import logo from "img/logo-color@2x.png";
import { ErrorView } from "common/components/error";
import { Tooltip } from "common/components/tooltip";
import { Placeholder } from "common/components/placeholder";
import { ToastWithActions } from "common/components/toastActions/toastActions";

import { FETCH_TRANSACTIONS } from "common/actions/analytics";
import { escapeForCSV, downloadCSV, copyToClipboard } from "common/utils";
import { txn_search } from "common/config/api";
import { pazienHistory } from "common/components/pazienHistory";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPazien } from "extensions/fa5-icons/faPazien";
import { faLinkAlt } from "extensions/fa5-icons/faLinkAlt";
import { faArrowToBottom } from "@fortawesome/pro-regular-svg-icons";
import { faPrint, faTimes } from "@fortawesome/pro-light-svg-icons";

import { single_txn_mapper } from "./fields";

import { TransactionMeta } from "./transactionMeta";

import "./singleTransaction.scss";

const mapStateToProps = (state) => ({
    merchant_config: state.common.merchant_config,
    canSeeMetaData: state.permissions.user_permissions.can_view_json_transaction_details,
    table_configs: state.components.table_configs.transactions
});

export const SingleTransaction = connect(mapStateToProps)(class extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: {},
            status: "loading",
            metaVisible: false
        };
        this.promises = [];
    }

    componentDidMount() {
        this.fetchTransaction();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.txn_id !== this.props.txn_id) {
            this.fetchTransaction();
        }
    }

    componentWillUnmount() {
        this.promises.forEach((promise) => promise.cancel());
    }

    fetchTransaction = ({ section, txn_id, txn_data } = this.props) => {
        if (txn_data) {
            this.setState({ status: "success", data: txn_data });
        } else {
            this.setState({
                status: "loading"
            }, () => {
                this.promises.push(FETCH_TRANSACTIONS({
                    url: txn_search[section],
                    id: txn_id
                }).then(([data]) => {
                    this.setState({ status: "success", data });
                }).catch((error) => {
                    this.setState({ status: "error", data: {} });
                }));
            });
        }
    };
    
    copyDirectLink = () => {
        const { origin } = window.location;
        const link = `${origin}/merchant/${pazienHistory.getCurrentFragment()}&txn_id=${this.props.txn_id}`;
        const showSuccessToast = () => {
            toast.success(<ToastWithActions
                actions={[
                    {
                        buttonText: "View in new tab",
                        action: () => { window.open(link, "_blank"); }
                    }
                ]}
                message="The transaction link has been copied to the clipboard."
            />);
        };
        const clipboardHelper = (str) => {
            // Don't send empty string. Use space instead.
            if (copyToClipboard(str === "" ? " " : str) === true) {
                showSuccessToast();
            } else {
                toast.warning("Something went wrong. Try again.");
            }
        };

        try {
            // Check if Clipboard API is available. If it's not available - use old method.
            if (navigator.clipboard) {
                // Try Clipboard API method, if it failed use old method.
                navigator.clipboard.writeText(link)
                    .then(showSuccessToast)
                    .catch(() => clipboardHelper(link));
            } else {
                clipboardHelper(link);
            }
        } catch {
            toast.error("Error copying item to clipboard.");
        }
    };

    exportTransaction = (e) => {
        const config = this.props.table_configs.pazien[this.props.section];
        const { fields } = single_txn_mapper({
            html: false, txn: this.state.data, section: this.props.section, fields: config.data
        });
        downloadCSV("Transaction_Detail.csv", fields.map(({ name, value }) => `${escapeForCSV(name)},${escapeForCSV(value)}`).join("\r\n"));
    };

    printTransaction = () => {
        this.setState({
            metaVisible: false
        }, () => {
            // add class to show header/footer
            document.getElementById("singleTransactionProps").classList.add("printing");
            printJS({
                printable: "singleTransactionProps",
                type: "html",
                // eslint-disable-next-line sentence-case/sentence-case
                font: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
                documentTitle: "Pazien"
            });
            // remove that class
            document.getElementById("singleTransactionProps").classList.remove("printing");
        });
    };

    toggleMetaVisibility = () => this.setState((prevState) => ({ metaVisible: !prevState.metaVisible }));
    
    static renderPlaceholder() {
        const renderFieldsGroup = (number) => times(number, (i) => (
            <div className="pair" key={i}>
                <div className="key"><Placeholder size="medium" noMargin /></div>
                <div className="value"><Placeholder noMargin /></div>
            </div>
        ));
        
        return (
            <div className="props" id="singleTransactionProps">
                <div className="txn-wrapper">
                    <div>
                        {renderFieldsGroup(3)}
                        <div className="spacer" />
                        {renderFieldsGroup(2)}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        // eslint-disable-next-line sentence-case/sentence-case
        const config = this.props.table_configs.pazien[this.props.section];
        const { title, merchant, date, fields } = single_txn_mapper({
            html: true, txn: this.state.data, section: this.props.section, fields: config.data
        });
        const { canSeeMetaData, section, txn_id } = this.props;
        const { metaVisible, status } = this.state;

        const renderFields = fields.map((field) => (
            <div key={field.name}>
                {field.subheader && <div className="subheader">{field.subheader}</div>}
                <div className="pair">
                    <div className="key">{ field.name }</div>
                    <div className="value">{ field.value }</div>
                </div>
                {field.spacer && <div className="spacer" />}
            </div>
        ));

        return (
            <div className={`single-transaction ${section} ${metaVisible && "wide"}`}>
                <header>
                    <div className="title">{title}</div>
                    <div className="bar">
                        {canSeeMetaData && (
                            <Tooltip title="Raw data">
                                <span onClick={this.toggleMetaVisibility}>
                                    <FontAwesomeIcon className="metaExplorer" icon={faPazien}/>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Copy transaction link to clipboard">
                            <span onClick={this.copyDirectLink}>
                                <FontAwesomeIcon className="copy" icon={faLinkAlt}/>
                            </span>
                        </Tooltip>
                        <Tooltip title="Export">
                            <span onClick={this.exportTransaction}>
                                <FontAwesomeIcon className="export" icon={faArrowToBottom}/>
                            </span>
                        </Tooltip>
                        <Tooltip title="Print">
                            <span onClick={this.printTransaction}>
                                <FontAwesomeIcon className="print" icon={faPrint}/>
                            </span>
                        </Tooltip>
                        <div className="divider" />
                        <Tooltip title="Close">
                            <span onClick={this.props.close}>
                                <FontAwesomeIcon className="close-trans" icon={faTimes}/>
                            </span>
                        </Tooltip>
                    </div>
                </header>
                {status === "error" && (
                    <ErrorView
                        header="Error"
                        message="There was a problem loading transaction data. Try again later."
                        hideDetails
                        captureInSentry={false}
                        custom
                    />
                )}
                
                {status === "loading" && SingleTransaction.renderPlaceholder()}
                
                {!metaVisible && status === "success" && (
                <div className="props" id="singleTransactionProps">
                    <header>
                        <div className="pazien-logo"><img alt="logo" src={logo}/></div>
                        <div className="www">www.pazien.com</div>
                        <div className="title">{ merchant } transaction detail</div>
                        <div className="prepared-by">Prepared by Pazien for {merchant} on {date}</div>
                    </header>
                    <div className="txn-wrapper">
                        {renderFields}
                    </div>
                </div>
                )}
                {metaVisible && status === "success" && (
                    <div className="meta" id="singleTransactionMeta">
                        <TransactionMeta section={section} id={txn_id} />
                    </div>
                )}
            </div>
        );
    }
});
